import { lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import posthog from 'posthog-js';
import { HelmetProvider } from 'react-helmet-async';

import BrowserBreakout from '@/components/BrowserBreakout';

// Layouts. Pages in the 2026-10 redesign share SiteLayout; the older pages keep their
// providers (Firebase auth, React Query) in LegacyShell, loaded only when one of them is.
const SiteLayout = lazy(() => import('@/site/SiteLayout'));
const LegacyShell = lazy(() => import('@/components/LegacyShell'));

// Pages in the new design.
const Home = lazy(() => import('@/app/page'));
const Platform = lazy(() => import('@/app/platform/page'));
const Research = lazy(() => import('@/app/research/page'));
const DoubtIndex = lazy(() => import('@/app/doubt-index/page'));
const Team = lazy(() => import('@/app/team/page'));
const Contact = lazy(() => import('@/app/contact/page'));
const Privacy = lazy(() => import('@/app/privacy/page'));
const PrivacyShopifyApp = lazy(() => import('@/app/privacy/shopify-app/page'));
const TermsShopifyApp = lazy(() => import('@/app/terms/shopify-app/page'));
const NotFoundPage = lazy(() => import('@/app/not-found/page'));

// Pages — lazy-loaded for per-route code-splitting (matches Next default).
const Analytics = lazy(() => import('@/app/analytics/page'));
const Audit = lazy(() => import('@/app/audit/page'));
const AuditLeads = lazy(() => import('@/app/audit/leads/page'));
const Blog = lazy(() => import('@/app/blog/page'));
const BlogPost = lazy(() => import('@/app/blog/[slug]/page'));
const BlueMonkey = lazy(() => import('@/app/bluemonkey/page'));
const Collections = lazy(() => import('@/app/collections/page'));
const CreatorsCommunity = lazy(() => import('@/app/creators_community/page'));
const Demo = lazy(() => import('@/app/demo/page'));
const GetStyled = lazy(() => import('@/app/get-styled/page'));
const GetStyledCustom = lazy(() => import('@/app/get-styled/custom-style/page'));
const Influencer = lazy(() => import('@/app/influencer/page'));
const MultiOutfit = lazy(() => import('@/app/multi-outfit/page'));
const Partner = lazy(() => import('@/app/partner/page'));
const Product = lazy(() => import('@/app/product/[id]/page'));
const Share = lazy(() => import('@/app/s/[shareId]/page'));
const ShareSocial = lazy(() => import('@/app/share-social/page'));
const Setup = lazy(() => import('@/app/setup/page'));
const SetupDashboard = lazy(() => import('@/app/setup/dashboard/page'));
const InstagramAlreadyConnected = lazy(() => import('@/app/setup/instagram/already-connected/page'));
const InstagramCallback = lazy(() => import('@/app/setup/instagram/callback/page'));
const InstagramConnect = lazy(() => import('@/app/setup/instagram/connect/page'));
const InstagramError = lazy(() => import('@/app/setup/instagram/error/page'));
const InstagramReturn = lazy(() => import('@/app/setup/instagram/return/page'));
const SetupLogin = lazy(() => import('@/app/setup/login/page'));
const SetupMessages = lazy(() => import('@/app/setup/messages/page'));
const Tryon = lazy(() => import('@/app/tryon/[id]/page'));
const Upload = lazy(() => import('@/app/upload/page'));

function PostHogInit() {
  useEffect(() => {
    const phKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!phKey) return;
    if (!('posthog' in window)) {
      posthog.init(phKey, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
        capture_pageview: 'history_change',
        person_profiles: 'identified_only',
      });
    }
    let userId = localStorage.getItem('stylepass_user_id');
    if (!userId) {
      userId = crypto.randomUUID();
      localStorage.setItem('stylepass_user_id', userId);
    }
    posthog.identify(userId);
  }, []);
  return null;
}

// A link to another page starts at its top (unless it targets an anchor); Back and Forward
// keep the browser's own scroll restore. It sits above both layouts because they unmount
// when a visitor moves between a new page and a legacy one.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  const last = useRef(pathname);
  useEffect(() => {
    if (last.current === pathname) return;
    last.current = pathname;
    if (!hash && navType !== 'POP') window.scrollTo(0, 0);
  }, [pathname, hash, navType]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserBreakout />
      <PostHogInit />
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/research" element={<Research />} />
            <Route path="/doubt-index" element={<DoubtIndex />} />
            <Route path="/team" element={<Team />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy/shopify-app" element={<PrivacyShopifyApp />} />
            <Route path="/terms/shopify-app" element={<TermsShopifyApp />} />
            {/* The same two pages at addresses without "shopify", for the App Store listing,
                whose form refuses any URL that contains it. The canonical stays the
                /shopify-app address (seo.json), which the app's own links use. */}
            <Route path="/privacy/app" element={<PrivacyShopifyApp />} />
            <Route path="/terms/app" element={<TermsShopifyApp />} />
            {/* Guessed addresses. Cloudflare 301s them at the edge (public/_redirects). */}
            <Route path="/about" element={<Navigate to="/team" replace />} />
            <Route path="/pricing" element={<Navigate to="/platform" replace />} />
            {/* Soft 404 (HTTP 200, noindex). Every legacy route below is explicit, so this
                only catches paths no group knows. */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route element={<LegacyShell />}>
            <Route path="/analytics" element={<Analytics />} />
            {/* Internal: reads the local dev server's request file, so never in a build. */}
            {process.env.NODE_ENV !== 'production' && <Route path="/audit/leads" element={<AuditLeads />} />}
            {/* The <Navigate> routes in this group are old pages the redesign replaced. Like
                /marketing, Cloudflare 301s each of them at the edge (public/_redirects); these
                keep dev, and any host that ignores _redirects, in step. */}
            <Route path="/app" element={<Navigate to="/" replace />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/bluemonkey" element={<BlueMonkey />} />
            <Route path="/book-a-demo" element={<Navigate to="/contact" replace />} />
            <Route path="/brands" element={<Navigate to="/platform" replace />} />
            <Route path="/collections" element={<Collections />} />
            <Route path="/creators_community" element={<CreatorsCommunity />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/get-styled" element={<GetStyled />} />
            <Route path="/get-styled/custom-style" element={<GetStyledCustom />} />
            <Route path="/influencer" element={<Influencer />} />
            <Route path="/landing-classic" element={<Navigate to="/" replace />} />
            <Route path="/landing-v2" element={<Navigate to="/" replace />} />
            <Route path="/manifesto" element={<Navigate to="/" replace />} />
            {/* The prototype served the old homepage at /marketing/; the address still
                leads home. Cloudflare 301s these at the edge (public/_redirects) so this
                only fires in dev and anywhere _redirects isn't honoured. */}
            <Route path="/marketing" element={<Navigate to="/" replace />} />
            <Route path="/multi-outfit" element={<MultiOutfit />} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/product/:id" element={<Product />} />
            <Route path="/s/:shareId" element={<Share />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/setup/dashboard" element={<SetupDashboard />} />
            <Route path="/setup/instagram/already-connected" element={<InstagramAlreadyConnected />} />
            <Route path="/setup/instagram/callback" element={<InstagramCallback />} />
            <Route path="/setup/instagram/connect" element={<InstagramConnect />} />
            <Route path="/setup/instagram/error" element={<InstagramError />} />
            <Route path="/setup/instagram/return" element={<InstagramReturn />} />
            <Route path="/setup/login" element={<SetupLogin />} />
            <Route path="/setup/messages" element={<SetupMessages />} />
            <Route path="/share-social" element={<ShareSocial />} />
            <Route path="/shopify" element={<Navigate to="/platform" replace />} />
            <Route path="/shopify2" element={<Navigate to="/" replace />} />
            <Route path="/shopify3" element={<Navigate to="/" replace />} />
            <Route path="/style-on-me" element={<Navigate to="/" replace />} />
            <Route path="/style-on-me/live" element={<Navigate to="/" replace />} />
            <Route path="/tryon/:id" element={<Tryon />} />
            <Route path="/upload" element={<Upload />} />
          </Route>
        </Routes>
      </Suspense>
    </HelmetProvider>
  );
}
